import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AppBar,
  Backdrop,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Popover,
  styled,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material"

import {
  ChevronLeftRounded,
  ChevronRightRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
  MenuRounded,
  TranslateRounded
} from "@mui/icons-material"

import HKVIcon from "../../HKVIcon/1.0"

import { useResizeDetector } from "react-resize-detector"

import packageJson from "../../../../package.json"
import { useSelector } from "react-redux"

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => !["persistent", "placement", "open", "size"].includes(prop),
})(({ theme, persistent, placement, open, size }) => {
  return (
    {
      display: "flex",
      flexDirection: "column",
      transition: "225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
      background: "#f5f5f5",
      boxShadow: theme.shadows[1],
      textAlign: "left",
      overflowY: "auto",
      overflowX: "hidden",
      position: "absolute",
      borderRadius: 0,

      top: 0,
      bottom: 0,
      ...(placement === "right") && {
        left: "unset",
        right: 0
      },
      ...(placement === "left") && {
        left: 0,
        right: "unset",
      },
      ...(!persistent && size === "large") && {
        // ...(open && size === "large") && {
        width: "80%",
        [theme.breakpoints.up("sm")]: {
          width: 520
        },
        [theme.breakpoints.up(1400)]: {
          width: 560
        }
      },
      ...(!persistent && size !== "large") && {
        // ...(open && size !== "large") && {
        width: "80%",
        [theme.breakpoints.up("sm")]: {
          width: 320
        },
        [theme.breakpoints.up(1400)]: {
          width: 360
        }
      },
      ...(persistent && {
        width: 56
      })
    }
  )
})

const StyledHeader = styled(AppBar, {
})(({ theme }) => {
  return {
    display: "flex",
    flexDirection: "row",
    height: 40,
    width: `calc(100% - ${theme.spacing(2)})`,
    padding: theme.spacing(0.5),
    margin: theme.spacing(1, 1, 0)
  }
})

const StyledHeaderImage = styled("img", {
  shouldForwardProp: (prop) => !["open"].includes(prop),
})(({ open, theme }) => {
  return {
    display: "flex",
    flexDirection: "column",
    background: "transparent",
    objectFit: "contain",
    margin: 0,
    maxWidth: open ? 32 : 40,
    maxHeight: open ? 32 : 40,

    aspectRatio: "1 / 1",

    "& svg": {
      color: theme.palette.primary.main
    }
  }
})

const StyledTypography = styled(Typography)({
  width: "100%",
  alignSelf: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
})

const StyledMenuItem = styled(Box, {
})(({ theme }) => {
  return {
    display: "flex",
    alignItems: "center",
    height: 40,
    padding: theme.spacing(1),
    boxSizing: "border-box",
    margin: theme.spacing(1),
    "&:first-of-type": {
      marginTop: 0
    }
  }
})

const StyledPopoverItem = styled("div", {
})(({ theme }) => {
  return {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(0, -2),
    padding: theme.spacing(0.5, 2),
    color: "inherit !important",
    textDecoration: "none",
    "&:hover": {
      background: theme.palette.action.hover
    }
  }
})

function HKVDrawer(props) {
  const { t } = useTranslation()
  const theme = useTheme()

  const accountinfo = useSelector(state => state.accountinfo)

  const {
    open = false,
    drawerStateChanged,
    header,
    items,
    logo,
    logout,
    offsetLeft,
    offsetTop,
    // onToggle,
    placement,
    doSignOut, doSignIn,
    zIndex,
    languages, selectedLanguage, setSelectedLanguage,
    persistent,
    subHeader,
    showButton,
    size
  } = props

  // const [open, setOpen] = useState()
  const [showBackdrop, setShowBackdrop] = useState(window.innerWidth < 600 && open)
  const [tooltipState, setTooltipState] = useState({})

  const [languageSelector, setLanguageSelector] = useState({ state: false, anchor: 0 })
  const currentLanguage = languages && languages.find(l => l.short === selectedLanguage)

  const onResize = () => {
    // claude: showBackdrop hier lokaal herberekenen i.p.v. de (nog niet ge-update) state lezen — React
    // state-updates zijn async, dus setShowBackdrop's nieuwe waarde is nog niet zichtbaar binnen deze
    // synchrone aanroep van onResize
    const isBackdropShown = open ? (window.innerWidth < 800) : false
    setShowBackdrop(isBackdropShown)

    const leafletControlsLeft = Array.from(document.getElementsByClassName("leaflet-left"))
    const leafletControlsRight = Array.from(document.getElementsByClassName("leaflet-right"))

    // if (persistent === true) return

    // claude: zodra de backdrop getoond wordt, ligt de drawer al over de hele kaart heen — dan hoeven de
    // leaflet-left/right-controls niet opzij te schuiven met een margin
    const newStyle = (open && !isBackdropShown)
      ? getMoveStyle()
      : { marginLeft: "unset", marginRight: "unset" }

    if (placement === "left") {
      leafletControlsLeft.forEach((leafletControl) => {
        Object.assign(leafletControl.style, newStyle)
      })
    }

    if (placement === "right") {
      leafletControlsRight.forEach((leafletControl) => {
        Object.assign(leafletControl.style, newStyle)
      })
    }
  }

  const { ref } = useResizeDetector({ skipOnMount: false, onResize })

  useEffect(() => {
    onResize()
  }, [open])

  const getMoveStyle = () => {
    let base = 0

    if (persistent) {
      base = -56
    }
    // } else
    if (open) {
      if (size === "large") {
        base += window.innerWidth >= 1400 ? 560
          : window.innerWidth >= 600 ? 520
            : 0
      } else {
        base += window.innerWidth >= 1400 ? 360
          : window.innerWidth >= 600 ? 320
            : 0
      }
    }

    if (placement === "left") {
      return { marginLeft: `${base}px`, marginRight: "unset" }
    }

    if (placement === "right") {
      return { marginRight: `${base}px`, marginLeft: "unset" }
    }

    return {}
  }

  // const drawerStateChanged = () => {
  //   onToggle && onToggle(placement, open)
  // }

  const renderIcon = () => {
    switch (placement) {
      case "right":
      case "right-extra":
        return open ? <ChevronRightRounded /> : <ChevronLeftRounded />

      case "bottom":
        return open ? <KeyboardArrowDownRounded /> : <KeyboardArrowUpRounded />

      case "left":
      case "left-extra":
      default:
        return open ? <ChevronLeftRounded /> : <ChevronRightRounded />
    }
  }

  const renderHeader = () => {
    if (!header) return null

    if (!persistent || (persistent && open)) {
      return (
        <StyledHeader
          position="relative"
          dir={placement.includes("right") ? "rtl" : "ltr"}
          elevation={1}
          sx={{ borderRadius: `${theme.shape.borderRadius}px` }}
        >
          {open && <React.Fragment>
            {header.image && <Tooltip title={`${header.title} - ${t("Version")}: ${packageJson.version}`} placement="right">
              <StyledHeaderImage rel="prefetch" src={header.image} alt={header.title} open={open} />
            </Tooltip>}
            <Divider sx={{ marginTop: theme.spacing(1), borderBottomWidth: 2 }} />
            <StyledTypography
              variant="h6"
              sx={{ margin: theme.spacing(0, 1), direction: "ltr" }}
              title={header.title}
            >
              {header.title}
            </StyledTypography>
          </React.Fragment>}
          <IconButton
            size="small"
            onClick={() => drawerStateChanged(placement)}
            sx={{ alignSelf: "center", flexShrink: 0, boxShadow: "none" }}
            color="secondary"
          >
            {renderIcon()}
          </IconButton>
        </StyledHeader>
      )
    } else {
      if ((header.button && header.button.visible === false) && header.image) {
        return <React.Fragment>
          <Tooltip placement="right" title={`${t("Version")}: ${packageJson.version}`}>
            <StyledHeaderImage src={header.image} alt={header.title} style={{ margin: theme.spacing(1) }} open={open} />
          </Tooltip>
          <Divider sx={{ margin: theme.spacing(0.5, 1, 1), borderBottomWidth: 2 }} />
        </React.Fragment>
      } else {
        return <React.Fragment>
          <IconButton
            sx={{ width: 40, height: 40, margin: theme.spacing(1) }}
            color="primary"
            onClick={() => drawerStateChanged(placement)}
            disabled={header.button ? Boolean(header.button.disabled) : false}
          >
            <MenuRounded />
          </IconButton>
          <div style={{ boxShadow: "0px 0px 0px 0.1px black", width: `calc(100% - ${theme.spacing(1.5)})`, margin: "-4px auto 4px" }} />
        </React.Fragment>
      }
    }
  }

  const renderSubHeader = () => {
    if (!subHeader) return

    return <React.Fragment>
      <Divider sx={{ margin: theme.spacing(1, 1, 0.5), borderBottomWidth: 2 }} />
      <StyledHeaderImage src={subHeader.image} alt={header.title} style={{ margin: theme.spacing(1) }} open={open} />
    </React.Fragment>
  }

  const toggleLngSelect = (event) => {
    setLanguageSelector(prevState => {
      prevState.state = !languageSelector.state
      prevState.anchor = event.currentTarget

      return { ...prevState }
    })
  }

  const renderLanguageSelector = () => {
    if (!languages || !currentLanguage) return null

    return (
      <div style={{ display: "flex", justifyContent: "center", margin: theme.spacing(2, 1), padding: "2px 7px" }}>
        <Tooltip title={t("Select_language")} placement="right">
          <div
            className="ellipsis"
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={toggleLngSelect}
          >
            <TranslateRounded fontSize="small" />
            {Boolean(open) && <Typography
              sx={{ margin: theme.direction === "rtl" ? theme.spacing(0, 0.5, 0, 0) : theme.spacing(0, 0, 0, 0.5) }}
              variant="body2"
            >
              {t(currentLanguage.long)}
            </Typography>}
          </div>
        </Tooltip>
        <Popover
          open={languageSelector.state}
          anchorEl={languageSelector.anchor}
          onClose={toggleLngSelect}
          anchorOrigin={{
            horizontal: "right",
            vertical: "top"
          }}
        >
          <Paper sx={{ padding: theme.spacing(1, 2) }}>
            {languages.map((language, index) => (
              <StyledPopoverItem
                key={index}
                className="transition"
                style={{
                  background: selectedLanguage === language.short && theme.palette.action.selected,
                  cursor: "pointer"
                }}
                onClick={(event) => {
                  setSelectedLanguage(language.short)
                  toggleLngSelect(event)
                }}
              >
                <Typography>{t(language.long)}</Typography>
              </StyledPopoverItem>
            ))}
          </Paper>
        </Popover>
      </div>
    )
  }

  const items1 = items?.filter(item => !item.placement || item.placement === "top")
  const items2 = items?.filter(item => item.placement === "bottom")

  return (
    <React.Fragment>
      <Drawer
        anchor={placement.includes("left") ? "left" : "right"}
        variant="persistent"
        slots={{
          paper: StyledPaper
        }}
        slotProps={{
          paper: {
            id: persistent ? null : `drawer-${placement}`,
            ref: ref,
            persistent: persistent,
            placement: placement,
            open: open,
            size: size
          }
        }}
        open={persistent ? true : Boolean(open)}
        sx={{
          "& >div": {
            zIndex: zIndex ? zIndex : 1200,
            "&:first-of-type": {
              marginLeft: offsetLeft ? offsetLeft : 0,
              marginTop: offsetTop ? offsetTop : 0,
              height: offsetTop ? `calc(100% - ${offsetTop})` : "100%"
            }
          }
        }}
      >
        {renderHeader()}
        <div style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          overflow: "auto"
        }}>
          {items1 && <div>
            {items1.map((item, index) => {
              const MenuItem = () => {
                // todo

                // const selected = item.hasOwnProperty("selected") ? item.selected : (item.path && location.pathname.startsWith(item.path))
                const selected = item.hasOwnProperty("selected")
                  ? item.selected
                  : (() => {
                    if (!item.path) return false

                    // if (item.path === "/import") {
                    //   return location.pathname === "/import"
                    // }

                    return (
                      location.pathname === item.path ||
                      location.pathname.startsWith(`${item.path}/`)
                    )
                  })()

                return (
                  <StyledMenuItem
                    component={open ? Paper : IconButton}
                    className="transition ellipsis"
                    sx={{
                      width: open ? "auto" : "40px",
                      cursor: item.disabled ? "auto" : "pointer",
                      opacity: item.disabled ? 0.5 : 1,
                      ...(selected) && {
                        background: "#eee"
                      },
                      "&:hover": {
                        background: item.disabled ? (open ? "#fff" : "unset") : "#eee"
                      }
                    }}
                    onClick={() => {
                      if (item.disabled) return
                      setTooltipState(prev => ({ ...prev, [index]: false }))
                      item.function(item)
                    }}
                  >
                    <HKVIcon iconName={item.icon} style={{ fill: selected ? theme.palette.primary.main : "#000", width: 24, height: 24, ...(item.iconStyle || {}) }} />
                    {Boolean(open) && <Typography
                      sx={{
                        color: selected ? theme.palette.primary.main : theme.palette.text.primary,
                        margin: theme.direction === "rtl" ? theme.spacing(0, 0.5, 0, 0) : theme.spacing(0, 0, 0, 0.5)
                      }}
                    >
                      {t(item.label)}
                    </Typography>}
                  </StyledMenuItem>
                )
              }

              const TooltipItem = ({ item }) => {
                const [open, setOpen] = useState(false)

                return (
                  <Tooltip
                    title={(item.disabled && item.disabledText) ? t(item.disabledText) : t(item.label)}
                    placement="right"
                    disableInteractive
                    open={open}
                    enterDelay={0}
                    leaveDelay={0}
                  >
                    <span
                      onMouseEnter={() => setOpen(true)}
                      onMouseLeave={() => setOpen(false)}
                    >
                      <MenuItem />
                    </span>
                  </Tooltip>
                )
              }

              return <TooltipItem key={index} item={item} />
            })}
          </div>}
          {props.children}
          {(logo || (languages && currentLanguage) || items2 || logout) && <div>
            {logo && <Tooltip
              title="HKV - website"
              followCursor={Boolean(open)}
              placement="right"
            >
              {open
                ? <img
                  rel="preload"
                  as="image"
                  src="/static/images/hkv-logo-192.png"
                  width="200px"
                  style={{ cursor: "pointer", margin: theme.spacing(1) }}
                  onClick={() => window.open("https://www.hkv.nl/", "_blank")}
                />
                : <StyledMenuItem
                  component={IconButton}
                  className="transition ellipsis"
                  sx={{
                    width: "40px",
                    cursor: "pointer",
                    opacity: 1,
                    "&:hover": {
                      background: "#eee"
                    }
                  }}
                  onClick={() => window.open("https://www.hkv.nl/", "_blank")}
                >
                  <HKVIcon iconName="HKVLogo" style={{ fill: theme.palette.primary.main }} />
                </StyledMenuItem>
              }
            </Tooltip>}
            {(languages && currentLanguage) && renderLanguageSelector()}
            {items2 && items2.map((item, index) => {
              const MenuItem = () => {
                const selected = item.selected

                return (
                  <StyledMenuItem
                    component={open ? Paper : IconButton}
                    className="transition ellipsis"
                    sx={{
                      width: open ? "auto" : "40px",
                      cursor: item.disabled ? "auto" : "pointer",
                      opacity: item.disabled ? 0.5 : 1,
                      ...(selected) && {
                        background: "#eee"
                      },
                      "&:hover": {
                        background: item.disabled ? (open ? "#fff" : "unset") : "#eee"
                      }
                    }}
                    onClick={() => {
                      if (item.disabled) return
                      setTooltipState(prev => ({ ...prev, [index]: false }))
                      item.function(item)
                    }}
                  >
                    <HKVIcon iconName={item.icon} style={{ fill: selected ? theme.palette.primary.main : "#000", width: 24, height: 24, ...(item.iconStyle || {}) }} />
                    {Boolean(open) && <Typography
                      sx={{
                        color: selected ? theme.palette.primary.main : theme.palette.text.primary,
                        margin: theme.direction === "rtl" ? theme.spacing(0, 0.5, 0, 0) : theme.spacing(0, 0, 0, 0.5)
                      }}
                    >
                      {t(item.label)}
                    </Typography>}
                  </StyledMenuItem>
                )
              }

              const TooltipItem = ({ item }) => {
                const [open, setOpen] = useState(false)

                return (
                  <Tooltip
                    title={(item.disabled && item.disabledText) ? t(item.disabledText) : t(item.label)}
                    placement="right"
                    disableInteractive
                    open={open}
                    enterDelay={0}
                    leaveDelay={0}
                  >
                    <span
                      onMouseEnter={() => setOpen(true)}
                      onMouseLeave={() => setOpen(false)}
                    >
                      <MenuItem />
                    </span>
                  </Tooltip>
                )
              }

              return <TooltipItem key={index} item={item} />
            })}
            {logout && <Tooltip
              title={accountinfo ? t("Logout") : t("Login")}
              followCursor={Boolean(open)}
              placement="right"
            >
              <span>
                <StyledMenuItem
                  component={open ? Paper : IconButton}
                  className="transition ellipsis"
                  sx={{
                    width: open ? "auto" : "40px",
                    cursor: "pointer",
                    opacity: 1,
                    "&:hover": {
                      background: open ? "unset" : "#eee"
                    }
                  }}
                  onClick={accountinfo ? doSignOut : doSignIn}
                >
                  {accountinfo
                    ? <HKVIcon iconName="LogoutRounded" style={{ fill: "rgba(0, 0, 0, 0.54)" }} />
                    : <HKVIcon iconName="LoginRounded" style={{ fill: "rgba(0, 0, 0, 0.54)" }} />
                  }
                  {open && <Typography sx={{ margin: theme.direction === "rtl" ? theme.spacing(0, 0.5, 0, 0) : theme.spacing(0, 0, 0, 0.5) }}>
                    {accountinfo ? accountinfo.name ? accountinfo.name : accountinfo.email : t("Login")}
                  </Typography>}
                </StyledMenuItem>
              </span>
            </Tooltip>}
          </div>}
        </div>
        {renderSubHeader()}
      </Drawer>
      <Backdrop open={showBackdrop} onClick={() => drawerStateChanged(placement)} sx={{ zIndex: 2, cursor: "pointer" }} />
      {(showBackdrop || persistent !== true && showButton !== false) && <div
        id="rotation-wrapper-outer"
        style={{
          display: "table",
          position: "absolute",
          right: placement.includes("right") ? 0 : "unset",
          left: placement.includes("left") ? 0 : "unset",
          top: 0,
          zIndex: 1,
          pointerEvents: "none"
        }}
      >
        <div
          id="rotation-wrapper-inner"
        >
          <Button
            dir="ltr"
            key={placement}
            size="small"
            color="primary"
            variant="contained"
            onClick={() => drawerStateChanged(placement)}
            sx={{
              display: "flex",
              transformOrigin: placement.includes("left") ? "top left" : "top right",
              transform: placement.includes("left") ? "rotate(-90deg) translate(-100%, 0%)" : "rotate(-90deg) translate(0%, -100%)",
              backfaceVisibility: "hidden",
              whiteSpace: "nowrap",
              borderRadius: placement.includes("left") ? `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px` : `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
              pointerEvents: "all",
              top: 100,
              padding: theme.spacing(0.25, 1)
            }}
            disableElevation
            endIcon={placement.includes("right") ? <KeyboardArrowUpRounded /> : null}
            startIcon={placement.includes("left") ? <KeyboardArrowDownRounded /> : null}
          >
            {header.title}
          </Button>
        </div>
      </div>}
    </React.Fragment>
  )
}

export default HKVDrawer